import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '../Tooltip';

import { CloseIcon, MenuIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from './icons';
import { LanguageToggle } from './LanguageToggle';
import { NotificationsBell } from './NotificationsBell';
import { ProfileMenu } from './ProfileMenu';
import { Sidebar } from './Sidebar';
import { ThemeToggle } from './ThemeToggle';
import type { BrandInfo, NavEntry } from './types';

interface Props {
  brand: BrandInfo;
  brandTo: string;
  nav: NavEntry[];
  settingsTo?: string;
  pageTitle?: string;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  tenantSwitcher?: ReactNode;
  notificationsTo?: string;
  signOutRedirect?: string;
  profilePath?: string;
  preferencesPath?: string;
}

export function Topbar({
  brand,
  brandTo,
  nav,
  settingsTo,
  pageTitle,
  mobileOpen,
  onMobileOpenChange,
  collapsed,
  onToggleCollapse,
  tenantSwitcher,
  notificationsTo,
  signOutRedirect,
  profilePath,
  preferencesPath,
}: Props): JSX.Element {
  const { t } = useTranslation('common');

  return (
    <header
      // top: var(--top-banner-h, 0) — set by WorkspaceShell when an
      // `topBanner` slot is mounted, so the topbar sticks BELOW the banner
      // instead of disappearing behind it. Falls back to 0 otherwise.
      style={{ top: 'var(--top-banner-h, 0px)' }}
      className="sticky z-20 flex h-14 items-center gap-2 border-b border-border bg-background/90 px-3 backdrop-blur md:px-6"
    >
      <Dialog.Root open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            aria-label={t('nav.open_menu')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
          >
            <MenuIcon size={20} />
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 md:hidden" />
          <Dialog.Content
            className="fixed inset-y-0 start-0 z-50 flex w-72 max-w-[85vw] flex-col border-e border-border bg-background shadow-xl outline-none md:hidden"
            aria-describedby={undefined}
          >
            <Dialog.Title className="sr-only">{t('nav.aria_main')}</Dialog.Title>
            <div className="flex items-center justify-end border-b border-border px-2 py-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label={t('nav.close_menu')}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <CloseIcon size={18} />
                </button>
              </Dialog.Close>
            </div>
            <Sidebar
              brand={brand}
              brandTo={brandTo}
              nav={nav}
              {...(settingsTo !== undefined ? { settingsTo } : {})}
              collapsed={false}
              {...(signOutRedirect !== undefined ? { signOutRedirect } : {})}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Tooltip label={collapsed ? t('nav.expand') : t('nav.collapse')}>
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          data-testid="sidebar-toggle"
          data-collapsed={collapsed}
          className="hidden h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:inline-flex"
        >
          {collapsed ? <PanelLeftOpenIcon size={18} /> : <PanelLeftCloseIcon size={18} />}
        </button>
      </Tooltip>

      {tenantSwitcher ? <div className="min-w-0">{tenantSwitcher}</div> : null}

      <div className="flex-1" />
      {pageTitle && pageTitle !== brand.name ? (
        <div className="hidden truncate text-sm font-medium text-foreground md:block">
          {pageTitle}
        </div>
      ) : null}

      <div className="flex items-center gap-1">
        <LanguageToggle />
        <ThemeToggle />
        {notificationsTo !== undefined ? (
          <NotificationsBell to={notificationsTo} />
        ) : null}
        <ProfileMenu
          {...(signOutRedirect !== undefined ? { signOutRedirect } : {})}
          {...(profilePath !== undefined ? { profilePath } : {})}
          {...(preferencesPath !== undefined ? { preferencesPath } : {})}
        />
      </div>
    </header>
  );
}
